import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { getPriceDisplaySettings } from './api';
import type { PriceDisplaySettings } from './types';
import { formatAmount, formatPrice } from './money';

const defaults: PriceDisplaySettings = {
  exchangeRate: '1',
  showPricesInUyu: false,
  updatedAt: null,
  updatedBy: null,
};
const PriceDisplayContext = createContext<{
  settings: PriceDisplaySettings;
  setSettings: (settings: PriceDisplaySettings) => void;
}>({ settings: defaults, setSettings: () => undefined });

export function PriceDisplayProvider({ children }: { children: React.ReactNode }) {
  const [settings, setSettings] = useState(defaults);
  useEffect(() => {
    let active = true;
    const refresh = () =>
      getPriceDisplaySettings()
        .then((value) => active && setSettings(value))
        .catch(() => undefined);
    refresh();
    const timer = window.setInterval(refresh, 30_000);
    return () => {
      active = false;
      window.clearInterval(timer);
    };
  }, []);
  const value = useMemo(() => ({ settings, setSettings }), [settings]);
  return <PriceDisplayContext.Provider value={value}>{children}</PriceDisplayContext.Provider>;
}

export function usePriceDisplay() {
  const context = useContext(PriceDisplayContext);
  const formatMoney = useCallback(
    (amount: number, currency: string) => {
      const usd = formatAmount(amount, currency);
      if (!context.settings.showPricesInUyu || currency !== 'USD') return usd;
      const uyus = convertMinorToUyu(amount, context.settings.exchangeRate);
      return `${usd} (≈ $U ${formatUyu(uyus)})`;
    },
    [context.settings],
  );
  const formatNullablePrice = useCallback(
    (amount: number | null) => (amount === null ? 'Sin cargar' : formatMoney(amount, 'USD')),
    [formatMoney],
  );
  return {
    settings: context.settings,
    setSettings: context.setSettings,
    formatMoney,
    formatNullablePrice,
  };
}

function convertMinorToUyu(amountMinor: number, rate: string): bigint {
  const [whole, fraction = ''] = rate.split('.');
  const scale = 10n ** BigInt(fraction.length);
  const rateScaled = BigInt(whole) * scale + BigInt(fraction || '0');
  const numerator = BigInt(amountMinor) * rateScaled;
  const denominator = 100n * scale;
  return (numerator + denominator / 2n) / denominator;
}

function formatUyu(value: bigint) {
  return value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.');
}
